import { useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import type { DesktopHistorySidebarHandle } from "@/features/history/components/DesktopHistorySidebar";
import {
  historyModeLabel,
  parseHistoryMode,
} from "@/features/history/historyMode";
import {
  createHistoryPageMediator,
  type HistoryPageEffect,
  type HistoryPageEvent,
  type HistoryPageEventNotifier,
  isHistoryPageEvent,
} from "@/features/history/historyPageMediator";
import {
  selectHistoryItemsForScope,
  useHistorySummaryScope,
} from "@/features/history/hooks/historySummaryScope";
import { useHistoryThreads } from "@/features/history/hooks/useHistoryThreads";
import { useHistoryStore } from "@/features/history/stores/historyStore";
import {
  selectActivePostedItems,
  usePostedHistoryStore,
} from "@/features/history/stores/postedHistoryStore";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useIsDesktop } from "@/shared/hooks/useMediaQuery";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export interface HistoryPageQueryPresentation {
  readonly data: ReturnType<typeof useHistoryThreads>["data"];
  readonly error: ReturnType<typeof useHistoryThreads>["error"];
  readonly isLoading: boolean;
  readonly isFetching: boolean;
}

export interface HistoryPagePresentation {
  readonly node: ReturnType<typeof useComponentNode>;
  readonly desktopHistorySidebarRef: React.RefObject<DesktopHistorySidebarHandle | null>;
  readonly mode: ReturnType<typeof parseHistoryMode>;
  readonly contentTitle: string;
  readonly historyItemsCount: number;
  readonly columns: number;
  readonly imageSize: number;
  readonly isDesktop: boolean;
  readonly isSettingsOpen: boolean;
  readonly query: HistoryPageQueryPresentation;
  readonly onEvent: HistoryPageEventNotifier;
}

export function useHistoryPagePresenter(): HistoryPagePresentation {
  const { mode: modeParam } = useParams({ from: "/history/$mode" });
  const routeMode = parseHistoryMode(modeParam);
  const navigate = useNavigate({ from: "/history/$mode" });
  const isDesktop = useIsDesktop();
  const historyScope = useHistorySummaryScope();
  const viewed = useHistoryStore((state) => state.viewed);
  const posted = usePostedHistoryStore((state) => state.posted);
  const columns = useCatalogStore((state) => state.columns);
  const imageSize = useCatalogStore((state) => state.imageSize);
  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  const navigateRef = useRef(navigate);
  const refetchRef = useRef<() => void>(() => undefined);
  const effectRunner = useRef<(effect: HistoryPageEffect) => void>(
    (): void => undefined,
  );
  navigateRef.current = navigate;

  const [mediator] = useState(() =>
    createHistoryPageMediator({
      initialMode: routeMode,
      isDesktop,
      runEffect: (effect: HistoryPageEffect): void => {
        effectRunner.current(effect);
      },
    }),
  );
  useHierarchyLifetime(mediator);

  const state = useMediatorSnapshot(mediator);
  const mode = state.mode;
  const historyItems = selectHistoryItemsForScope(
    mode === "posted" ? selectActivePostedItems(posted) : viewed,
    historyScope,
  );
  const query = useHistoryThreads(mode, historyItems, historyScope);
  refetchRef.current = (): void => {
    void query.refetch();
  };
  effectRunner.current = (effect: HistoryPageEffect): void => {
    switch (effect.type) {
      case "navigate-mode":
        void navigateRef.current({
          to: "/history/$mode",
          params: { mode: effect.mode },
        });
        return;
      case "refresh":
        refetchRef.current();
        return;
      case "toggle-sidebar":
        desktopHistorySidebarRef.current?.toggle();
        return;
      case "scroll":
        window.scrollTo({
          top:
            effect.edge === "top" ? 0 : document.documentElement.scrollHeight,
        });
    }
  };

  const node = useComponentNode((event) => {
    if (!isHistoryPageEvent(event)) return "unhandled";
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const onEvent = useCallback<HistoryPageEventNotifier>(
    (event: HistoryPageEvent): ReturnType<HistoryPageEventNotifier> =>
      node.dispatch(event).status,
    [node],
  );

  useEffect(() => {
    const event: HistoryPageEvent = {
      type: "history/route-synchronized",
      mode: routeMode,
    };
    node.dispatch(event);
  }, [node, routeMode]);

  useEffect(() => {
    const event: HistoryPageEvent = {
      type: "history/viewport-synchronized",
      isDesktop,
    };
    node.dispatch(event);
  }, [isDesktop, node]);

  const contentTitle = historyModeLabel(mode);
  useDocumentTitle(`${contentTitle} - ${BOARD_DISPLAY_NAME}`);

  return {
    node,
    desktopHistorySidebarRef,
    mode,
    contentTitle,
    historyItemsCount: historyItems.length,
    columns,
    imageSize,
    isDesktop,
    isSettingsOpen: state.isSettingsOpen,
    query: {
      data: query.data,
      error: query.error,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
    },
    onEvent,
  };
}
